// Aggregates pre-computed data into a single context object for the advisory
// report AI calls. The model should never invent data. Everything it narrates
// must originate in the structure this function returns.
//
// POC scope: executive-summary only. Extend categorySummary/benchmark/etc.
// as later sections are added.

export function buildAdvisoryReportContext({
  client,
  assessment,
  scores,
  benchmarkData,
  alignments,
  priorities,
  clientEnrichment,
  applicableFrameworks
}) {
  const categorySummary = scores?.maturity?.categoryScores
    ? Object.entries(scores.maturity.categoryScores)
        .map(([id, d]) => ({
          id,
          name: d.name,
          score: Math.round(d.score || 0),
          maturityLevel: d.maturityLevel?.label || 'Initial'
        }))
        .sort((a, b) => a.score - b.score)
    : []

  const riskAppetiteSummary = scores?.riskAppetite
    ? {
        averageAppetite: scores.riskAppetite.averageAppetite,
        averageLabel: scores.riskAppetite.averageLabel,
        domains: Object.fromEntries(
          Object.entries(scores.riskAppetite.domains || {}).map(([k, v]) => [
            k,
            { score: v.score, label: v.label }
          ])
        )
      }
    : null

  const benchmarkSummary = benchmarkData
    ? {
        industryAverage: benchmarkData.benchmark,
        clientScore: benchmarkData.clientScore,
        gap: benchmarkData.gap,
        severity: benchmarkData.severity?.label,
        industryName: benchmarkData.industryInfo?.name,
        keyFrameworks: benchmarkData.industryInfo?.keyFrameworks || []
      }
    : null

  const alignmentsSummary = (alignments || [])
    .filter(a => a.isConcern)
    .slice(0, 5)
    .map(a => ({
      domain: a.domain,
      riskAppetite: a.riskAppetite?.label,
      maturityAverage: Math.round(a.relatedMaturity?.average || 0),
      gap: a.gap,
      alignment: a.alignment?.label
    }))

  const prioritiesSummary = (priorities || []).slice(0, 5).map(p => ({
    title: p.title || p.name,
    severity: p.severity || p.priority,
    rationale: p.rationale || p.reason,
    category: p.category
  }))

  const businessScopingSummary = assessment?.responses?.businessScoping
    ? summarizeBusinessScoping(assessment.responses.businessScoping)
    : null

  const enrichmentSummary = clientEnrichment?.enrichment
    ? {
        industry: clientEnrichment.enrichment.companyProfile?.industry,
        subIndustry: clientEnrichment.enrichment.companyProfile?.subIndustry,
        description: clientEnrichment.enrichment.companyProfile?.description,
        estimatedSize: clientEnrichment.enrichment.companyProfile?.estimatedSize,
        regulatoryFrameworks:
          clientEnrichment.enrichment.regulatoryProfile?.applicableFrameworks || [],
        dataTypes: clientEnrichment.enrichment.regulatoryProfile?.dataTypes || [],
        securityGrade: clientEnrichment.enrichment.securityPosture?.overallGrade,
        keyRisks: clientEnrichment.enrichment.engagementContext?.keyRisks || [],
        suggestedFocusAreas:
          clientEnrichment.enrichment.engagementContext?.suggestedFocusAreas || []
      }
    : null

  return {
    client: {
      name: client?.name,
      industry: client?.industry,
      subIndustry: client?.industrySubCategory || client?.industry_sub_category,
      staffingSize: client?.staffingSize || client?.staffing_size,
      annualRevenue: client?.annualRevenue || client?.annual_revenue,
      itTeamSize: client?.itTeamSize || client?.it_team_size,
      geographicalPresence: client?.geographicalPresence || client?.geographical_presence
    },
    scores: {
      overall: {
        score: Math.round(scores?.overall?.score || 0),
        maturityLabel: scores?.overall?.maturityLevel?.label || 'Initial',
        maturityLevel: scores?.overall?.maturityLevel?.level
      },
      categorySummary,
      riskAppetite: riskAppetiteSummary
    },
    benchmark: benchmarkSummary,
    alignments: alignmentsSummary,
    priorities: prioritiesSummary,
    applicableFrameworks: applicableFrameworks || [],
    enrichment: enrichmentSummary,
    businessScopingSummary
  }
}

// Extracts the handful of business-scoping answers that matter for the
// executive summary. Keys are the question IDs defined in businessScoping.json;
// only the ones we can confidently map are pulled.
function summarizeBusinessScoping(responses) {
  const get = (id) => responses?.[id]?.value ?? responses?.[id]
  return {
    industry: get('bs-0'),
    employees: get('bs-1'),
    annualRevenue: get('bs-2'),
    geography: get('bs-3'),
    publiclyTraded: get('bs-4'),
    dataTypes: get('bs-5'),
    criticalBusinessFunctions: get('bs-6')
  }
}

// Helper: pick the top-3 weakest categories by score for "top risks" rendering.
export function pickWeakestCategories(scores, count = 3) {
  if (!scores?.maturity?.categoryScores) return []
  return Object.entries(scores.maturity.categoryScores)
    .map(([id, d]) => ({ id, name: d.name, score: Math.round(d.score || 0) }))
    .sort((a, b) => a.score - b.score)
    .slice(0, count)
}

// Helper: pick the top-3 strongest categories for "key strengths" rendering.
export function pickStrongestCategories(scores, count = 3) {
  if (!scores?.maturity?.categoryScores) return []
  return Object.entries(scores.maturity.categoryScores)
    .map(([id, d]) => ({ id, name: d.name, score: Math.round(d.score || 0) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, count)
}
